import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'

import { useToastContext } from '../../../../../contexts'
import {
  DeliveryForecastDespatchAdviceItem,
  DeliveryForecastDespatchAdviceItemBatch,
} from '../../../../../data/models'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../utils/helper/calculateBreakpointWidth'
import {
  requestCreateDeliveryForecastDespatchAdviceItemBatch,
  requestDeleteDeliveryForecastDespatchAdviceItemBatch,
  requestUpdateDeliveryForecastDespatchAdviceItemBatch,
} from '../../../data/api'
import {
  useAllDeliveryForecastDespatchAdviceItemBatches,
  useAllDeliveryForecastDespatchAdviceItemPackagingItems,
  useAllDeliveryForecastDespatchAdviceItemPackagings,
} from '../../../data/hooks'
import { DataForArticlePutDto, PackagingNode } from '../../../data/model'
import { countUsedBatches } from '../../../utils'
import { submitDeliveryForecastDespatchAdviceItem } from '../ArticleDetailForm/ArticleDetailForm.data'
import { ConfirmationBatchesTableForm } from './ConfirmationBatchesTable.form'

type Props = {
  despatchAdviceId: string
  itemId: string
  item: DeliveryForecastDespatchAdviceItem
  items: DeliveryForecastDespatchAdviceItem[]
  isLoading: boolean
}

export const ConfirmationBatchesTableData: React.FC<Props> = ({
  despatchAdviceId,
  itemId,
  item,
  items,
  isLoading,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { showToast } = useToastContext()

  const [isBulkDeleteRunning, setIsBulkDeleteRunning] = useState(false)
  const [loadingRowIds, setLoadingRowIds] = useState<Set<string>>(new Set())

  // Get all batches
  const {
    data: batches,
    isLoading: isBatchesLoading,
    error,
  } = useAllDeliveryForecastDespatchAdviceItemBatches(despatchAdviceId, itemId)

  // Get all packagings
  const { data: packagings, isLoading: isPackagingsLoading } =
    useAllDeliveryForecastDespatchAdviceItemPackagings(despatchAdviceId, itemId)

  // Get packaging nodes
  const { data: packaging, isLoading: isPackagingLoading } =
    useAllDeliveryForecastDespatchAdviceItemPackagingItems(
      despatchAdviceId,
      itemId,
      packagings && packagings[0] && packagings?.[0].Id,
    )

  const usedBatches = countUsedBatches(packaging)

  const getDisabledFieldIndexByName = (name: string): number => {
    const foundIndex = item.Meta?.Fields?.DisabledCalculation?.findIndex(
      p => p.Fieldname === name,
    )

    return foundIndex || -1
  }

  const getDisabledFieldValueByName = (name: string): boolean => {
    const foundIndex = getDisabledFieldIndexByName(name)
    if (foundIndex < 0) return false

    const values = item.Meta?.Fields?.DisabledCalculation || []
    return values[foundIndex].IsDisabled || false
  }

  const replaceDisabledFields = (name: string, value: boolean) => {
    const values = [...(item.Meta?.Fields?.DisabledCalculation || [])]
    const foundIndex = values.findIndex(p => p.Fieldname === name)
    if (foundIndex >= 0) {
      values.splice(foundIndex, 1)
    }

    values.push({ Fieldname: name, IsDisabled: value })
    return values
  }

  // Functions for handling the loading states of the buttons
  const addLoadingRowIds = (rowIds: string[]) => {
    setLoadingRowIds(prevState => {
      const updatedSet = new Set(prevState)
      rowIds.forEach(rowId => updatedSet.add(rowId))
      return updatedSet
    })
  }

  const removeLoadingRowIds = (rowIds: string[]) => {
    setLoadingRowIds(prevState => {
      const updatedSet = new Set(prevState)
      rowIds.forEach(rowId => updatedSet.delete(rowId))
      return updatedSet
    })
  }

  const hasLoadingRowId = (rowId: string) => {
    return loadingRowIds.has(rowId)
  }

  // Functions for creating, saving or deleting batches
  const handleCreate = async (
    batch: DeliveryForecastDespatchAdviceItemBatch,
  ) => {
    try {
      addLoadingRowIds([batch.Id])

      await requestCreateDeliveryForecastDespatchAdviceItemBatch(
        despatchAdviceId,
        itemId,
        batch,
      )

      showToast({
        severity: 'success',
        summary: t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.add.messages.success.summary',
        ),
        detail: t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.add.messages.success.detail',
          {
            batchnumber: batch?.Batchnumber,
            quantity: batch?.Quantity?.toLocaleString(language),
          },
        ),
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.add.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      removeLoadingRowIds([batch.Id])
    }
  }

  const handleSave = async (batch: DeliveryForecastDespatchAdviceItemBatch) => {
    try {
      addLoadingRowIds([batch.Id])

      await requestUpdateDeliveryForecastDespatchAdviceItemBatch(
        despatchAdviceId,
        itemId,
        batch.Id,
        batch,
      )

      showToast({
        severity: 'success',
        summary: t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.save.messages.success.summary',
        ),
        detail: t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.save.messages.success.detail',
          {
            batchnumber: batch?.Batchnumber,
            quantity: batch?.Quantity?.toLocaleString(language),
          },
        ),
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.save.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      removeLoadingRowIds([batch.Id])
    }
  }

  const handleDelete = async (
    batches: DeliveryForecastDespatchAdviceItemBatch[],
    callback?: () => void,
  ) => {
    const batchIds = batches.map(batch => batch.Id)
    const usedIdBatches = Object.keys(countUsedBatches(packaging))
    const conflictingBatches = batches.filter(batch =>
      usedIdBatches.includes(batch.Batchnumber),
    )

    try {
      addLoadingRowIds(batchIds)
      setIsBulkDeleteRunning(batchIds.length > 1)

      const handleConflict = () => {
        const confirmDelete = () =>
          confirmDialog({
            header: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.delete.dialog.title',
              {
                count: conflictingBatches.length,
              },
            ),
            message: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.delete.dialog.message',
              {
                count: conflictingBatches.length,
                batchnumbers: conflictingBatches
                  .map(conflictedBatch => conflictedBatch.Batchnumber)
                  .join(', '),
              },
            ),
            icon: 'pi pi-info-circle',
            acceptClassName: 'p-button-danger',
            accept: deleteBatches,
            acceptLabel: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.delete.dialog.delete.label',
            ),
            rejectLabel: t(
              'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.delete.dialog.cancel.label',
            ),
            style: { width: '36rem' },
            breakpoints: { [calculateBreakpointWidth(36)]: '90vw' },
          })

        if (!confirmDelete()) return
      }

      const deleteBatches = async () => {
        const deleteRequests = batches.map(batch =>
          requestDeleteDeliveryForecastDespatchAdviceItemBatch(
            despatchAdviceId,
            itemId,
            batch.Id,
          ),
        )

        await Promise.all(deleteRequests)

        if (callback) callback()

        showToast({
          severity: 'success',
          summary: t(
            'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.delete.dialog.delete.messages.success.summary',
            {
              count: batches.length,
            },
          ),
          detail: t(
            'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.delete.dialog.delete.messages.success.detail',
            {
              count: batches?.length,
              batchnumber: batches?.[0].Batchnumber,
            },
          ),
        })
      }

      if (conflictingBatches.length > 0) {
        await handleConflict()
      } else {
        await deleteBatches()
      }
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'deliveryForecastDespatchAdvice.articleDetail.confirmationBatches.table.delete.dialog.delete.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      removeLoadingRowIds(batchIds)
      setIsBulkDeleteRunning(false)
    }
  }

  return (
    <>
      <ConfirmationBatchesTableForm
        batches={batches}
        usedBatches={usedBatches}
        isLoading={isLoading}
        hasLoadingRowId={hasLoadingRowId}
        isBulkDeleteRunning={isBulkDeleteRunning}
        handleCreate={handleCreate}
        handleSave={handleSave}
        handleDelete={handleDelete}
      />

      <ConfirmDialog />
    </>
  )
}
